import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  HiClipboardDocumentList,
  HiTableCells,
  HiCodeBracket,
  HiCpuChip,
  HiDocumentText,
  HiArrowRight,
  HiSparkles,
} from 'react-icons/hi2';
import ProductDevelopmentAuditTool from './ProductDevelopmentAuditTool';
import StageSubmissionForm from './StageSubmissionForm';
import SubmissionsTable from './SubmissionsTable';
import { getUserSubmissions, CATEGORIES } from '../../services/productDevelopmentService';
import { useAuth } from '../../context/AuthContext';
import ClientSubmissionsPanel from './ClientSubmissionsPanel';
import useRole from '../../hooks/useRole';

const ProductDevelopment = ({ userRole = 'client' }) => {
  const { currentUser } = useAuth();
  const { isStaff, permissions, userRole: resolvedRole } = useRole();
  const [activeTab, setActiveTab] = useState(() => {
    // If we can determine staff status immediately, use it
    const staffRoles = [
      'admin',
      'moderator',
      'businessadvisor',
      'coach',
      'superadmin',
      'superadmins',
    ];
    if (resolvedRole && staffRoles.includes(resolvedRole.toLowerCase())) {
      return 'reviews';
    }
    return 'development';
  });
  const [activeCategory, setActiveCategory] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [showSubmissionsTable, setShowSubmissionsTable] = useState(false);

  const showAuditTool = isStaff;
  const isClient = !isStaff;

  // Update tab when role is resolved - staff should see submissions, never client forms
  useEffect(() => {
    if (isStaff) {
      // Force staff to see submissions tab, never development forms
      if (activeTab === 'development') {
        setActiveTab('reviews');
      }
    }
  }, [isStaff, activeTab]);

  useEffect(() => {
    if (isClient && currentUser?.uid) {
      loadSubmissions();
    }
  }, [isClient, currentUser?.uid]);

  const loadSubmissions = async () => {
    if (!currentUser?.uid) return;
    setLoadingSubmissions(true);
    try {
      const data = await getUserSubmissions(currentUser.uid);
      setSubmissions(data);
    } catch (error) {
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleSubmissionSuccess = () => {
    loadSubmissions();
    setShowSubmissionForm(false);
    setActiveCategory(null);
  };

  const handleCategorySelect = category => {
    setActiveCategory(category);
    setShowSubmissionForm(true);
  };

  const handleOpenForm = () => {
    setShowSubmissionForm(true);
    // Don't set category - let user choose in form
  };

  const getCategorySubmissionCount = category => {
    return submissions.filter(s => s.category === category).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <h1 className="mb-3 text-4xl font-bold text-black md:text-5xl">Product Development</h1>
          <p className="text-lg text-gray-600">
            Document and track your software or non-software product development journey
          </p>
        </motion.div>

        {/* Staff Tabs - Only show for staff */}
        {showAuditTool && (
          <div className="mb-8 rounded-xl border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('development')}
                  className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'development'
                      ? 'border-blue-600 text-black'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                  }`}
                >
                  <HiSparkles className="h-5 w-5" />
                  <span>Development</span>
                </button>
                <button
                  onClick={() => setActiveTab('audit')}
                  className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'audit'
                      ? 'border-blue-600 text-black'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                  }`}
                >
                  <HiClipboardDocumentList className="h-5 w-5" />
                  <span>Audit Tool</span>
                </button>
                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'reviews'
                      ? 'border-blue-600 text-black'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                  }`}
                >
                  <HiTableCells className="h-5 w-5" />
                  <span>Client Submissions</span>
                </button>
              </nav>
            </div>
          </div>
        )}

        {/* Content based on active tab */}
        {/* CRITICAL: Staff should NEVER see client forms - only tabs content */}
        {isStaff ? (
          activeTab === 'audit' ? (
            <ProductDevelopmentAuditTool userRole={userRole} />
          ) : (
            <ClientSubmissionsPanel currentUser={currentUser} userRole={userRole} />
          )
        ) : // Clients see development forms - only show if development tab is active
        activeTab === 'development' ? (
          <div className="space-y-8">
            {/* Category Selection Cards - Beautiful Design */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Software Development Card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="group relative overflow-hidden rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50 shadow-xl transition-all duration-300 hover:border-blue-400 hover:shadow-2xl"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="relative p-8">
                  <div className="mb-6 flex items-center justify-between">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 shadow-lg">
                      <HiCodeBracket className="h-8 w-8 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-black">
                        {getCategorySubmissionCount(CATEGORIES.SOFTWARE)}
                      </div>
                      <div className="text-xs text-gray-600">Submissions</div>
                    </div>
                  </div>
                  <h3 className="mb-3 text-2xl font-bold text-black">Software Development</h3>
                  <p className="mb-6 text-gray-600">
                    Document your software system architecture, APIs, databases, frameworks,
                    security measures, and technical implementation details.
                  </p>
                  <motion.button
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleCategorySelect(CATEGORIES.SOFTWARE)}
                    className="flex w-full items-center justify-between rounded-xl bg-blue-600 px-6 py-4 text-white shadow-lg transition-all hover:bg-blue-700 hover:shadow-xl"
                  >
                    <span className="font-semibold">Start Software Submission</span>
                    <HiArrowRight className="h-5 w-5" />
                  </motion.button>
                </div>
              </motion.div>

              {/* Non-Software Development Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="group relative overflow-hidden rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 via-white to-purple-50 shadow-xl transition-all duration-300 hover:border-purple-400 hover:shadow-2xl"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="relative p-8">
                  <div className="mb-6 flex items-center justify-between">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-600 shadow-lg">
                      <HiCpuChip className="h-8 w-8 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-black">
                        {getCategorySubmissionCount(CATEGORIES.NON_SOFTWARE)}
                      </div>
                      <div className="text-xs text-gray-600">Submissions</div>
                    </div>
                  </div>
                  <h3 className="mb-3 text-2xl font-bold text-black">Non-Software Products</h3>
                  <p className="mb-6 text-gray-600">
                    Document your hardware devices, physical products (teas, soaps, textiles),
                    manufacturing processes, product specifications, quality control, packaging, and
                    distribution channels.
                  </p>
                  <motion.button
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleCategorySelect(CATEGORIES.NON_SOFTWARE)}
                    className="flex w-full items-center justify-between rounded-xl bg-purple-600 px-6 py-4 text-white shadow-lg transition-all hover:bg-purple-700 hover:shadow-xl"
                  >
                    <span className="font-semibold">Start Non-Software Submission</span>
                    <HiArrowRight className="h-5 w-5" />
                  </motion.button>
                </div>
              </motion.div>
            </div>

            {/* My Submissions Section */}
            {isClient && submissions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="rounded-2xl border border-gray-200 bg-white shadow-lg"
              >
                <div className="border-b border-gray-200 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-black">My Submissions</h2>
                      <p className="text-sm text-gray-600">
                        View and manage your product development submissions
                      </p>
                    </div>
                    <button
                      onClick={() => setShowSubmissionsTable(!showSubmissionsTable)}
                      className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50 hover:shadow-md"
                    >
                      <HiTableCells className="h-4 w-4" />
                      {showSubmissionsTable ? 'Hide' : 'View'} All
                    </button>
                  </div>
                </div>
                {showSubmissionsTable && (
                  <div className="p-6">
                    <SubmissionsTable
                      submissions={submissions}
                      loading={loadingSubmissions}
                      onRefresh={loadSubmissions}
                    />
                  </div>
                )}
              </motion.div>
            )}

            {/* Empty State for Clients */}
            {isClient && submissions.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center"
              >
                <HiDocumentText className="mx-auto mb-4 h-16 w-16 text-gray-400" />
                <h3 className="mb-2 text-xl font-semibold text-gray-700">No Submissions Yet</h3>
                <p className="text-gray-600">
                  Get started by selecting Software or Non-Software Development above to create your
                  first submission.
                </p>
              </motion.div>
            )}
          </div>
        ) : null}

        {/* Submission Form Modal */}
        {isClient && (
          <StageSubmissionForm
            stage="development"
            category={activeCategory}
            isOpen={showSubmissionForm}
            onClose={() => {
              setShowSubmissionForm(false);
              setActiveCategory(null);
            }}
            onSuccess={handleSubmissionSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default ProductDevelopment;
